import { ScanInlineProduct } from '../CommonScanSchemas';
import { ScanLineItem } from '../ScanRoot';
import {
  getOptimizedLineItem,
  getScanOption,
  getScanTask,
  getScanWithUpdatedOption,
  getScanWithUpdatedTask,
  getScopeForScanPartChildren,
  getUpdatedLineItem,
  getUpdatedOption,
  ScanRootOptimized,
} from '../ScanRootOptimized';
import { getScanTemplateOption, ScanTemplateLineItem } from '../ScanTemplateRoot';
import { initDeoptimizedStandardLineItem, initStandardOptionFromTemplate } from './StandardScanInit';
type InlineProductsById = Record<string, ScanInlineProduct>;

/**
 * Adds a new option to a task in a scan, based on a template option.
 * Optionally adds a line item to the new option.
 * @param param0
 * @returns
 */
export const addStandardScanOption = ({
  scan,
  groupId,
  taskId,
  optionTemplateId,
  inlineProductsById,
  addLineItemTemplateId,
  addLineItemInitializers,
  agentUpdatedAt,
  now = Date.now(),
}: {
  scan: ScanRootOptimized;
  groupId: string;
  taskId: string;
  optionTemplateId: string;
  inlineProductsById: InlineProductsById;
  addLineItemTemplateId?: string;
  addLineItemInitializers?: Partial<ScanLineItem>;
  agentUpdatedAt?: number;
  now?: number;
}): ScanRootOptimized => {
  const task = getScanTask(scan, groupId, taskId);
  const scopeForOption = getScopeForScanPartChildren(task);
  const optionTemplate = getScanTemplateOption(task.template, optionTemplateId);

  let newOption = initStandardOptionFromTemplate(optionTemplate, scopeForOption, inlineProductsById, now, agentUpdatedAt);
  if (addLineItemTemplateId) {
    const lineItemTemplate = optionTemplate.lineItemTemplates?.find((lt: ScanTemplateLineItem) => lt.id === addLineItemTemplateId);
    if (!lineItemTemplate) throw new Error(`Line item template with id ${addLineItemTemplateId} not found`);
    const newLineItem = initDeoptimizedStandardLineItem(lineItemTemplate, inlineProductsById, now, agentUpdatedAt);
    const scopeForLineItem = getScopeForScanPartChildren(newOption);
    let optimizedLineItem = getOptimizedLineItem(newLineItem, lineItemTemplate, scopeForLineItem);
    if (addLineItemInitializers) {
      optimizedLineItem = getUpdatedLineItem(groupId, taskId, newOption.id, optimizedLineItem, addLineItemInitializers, scopeForLineItem, now);
    }
    newOption = getUpdatedOption(
      groupId,
      taskId,
      newOption,
      {
        lineItems: [...(newOption.lineItems ?? []), optimizedLineItem],
      },
      scopeForOption,
      now,
    );
  }

  return getScanWithUpdatedTask(
    scan,
    groupId,
    taskId,
    { options: [...(task.options ?? []), newOption] },
    now,
  );
};

export const addStandardScanLineItem = (
  scan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  optionId: string,
  lineItemTemplateId: string,
  inlineProductsById: InlineProductsById,
  lineItemInitializers?: Partial<ScanLineItem>,
  agentUpdatedAt?: number,
  now?: number,
): ScanRootOptimized => {
  const option = getScanOption(scan, groupId, taskId, optionId);
  const lineItemTemplate = option.template.lineItemTemplates?.find((lt: ScanTemplateLineItem) => lt.id === lineItemTemplateId);
  if (!lineItemTemplate) throw new Error(`Line item template with id ${lineItemTemplateId} not found`);
  const newLineItem = initDeoptimizedStandardLineItem(lineItemTemplate, inlineProductsById, now, agentUpdatedAt);
  let optimizedLineItem = getOptimizedLineItem(newLineItem, lineItemTemplate, getScopeForScanPartChildren(option));
  if (lineItemInitializers) {
    optimizedLineItem = getUpdatedLineItem(groupId, taskId, option.id, optimizedLineItem, lineItemInitializers, getScopeForScanPartChildren(option), now);
  }
  return getScanWithUpdatedOption(scan, groupId, taskId, optionId, {
    lineItems: [
      ...(option.lineItems ?? []),
      optimizedLineItem,
    ],
  }, now);
};

/**
 * Upserts a standard scan option in a scan, using the provided template IDs.
 * If the option already exists, it will be updated; if it does not exist,
 * it will be created. Optionally adds a line item to the option.
 * @param param0 An object containing the scan, template IDs, inline products,
 * and optional line item details.
 * @returns A new ScanRootOptimized with the upserted option (and line item,
 * if specified).
 */
export const upsertStandardScanOption = ({
  scan,
  groupId,
  taskId,
  optionTemplateId,
  inlineProductsById,
  addLineItemTemplateId,
  addLineItemInitializers,
  agentUpdatedAt,
  now = Date.now(),
}: {
  scan: ScanRootOptimized;
  groupId: string;
  taskId: string;
  optionTemplateId: string;
  inlineProductsById: InlineProductsById;
  addLineItemTemplateId?: string;
  addLineItemInitializers?: Partial<ScanLineItem>;
  agentUpdatedAt?: number;
  now?: number;
}): ScanRootOptimized => {
  const scanGroup = scan.groups.find(g => g.id === groupId);
  if (!scanGroup) throw new Error(`Group with id ${groupId} not found`);
  const scanGroupId = scanGroup.id;

  const scanTask = getScanTask(scan, groupId, taskId);
  if (!scanTask) throw new Error(`Task with id ${taskId} not found in group ${scanGroupId}`);

  const scanOption = scanTask.options.find(o => o.template.id === optionTemplateId);
  if (!scanOption) {
    return addStandardScanOption({
      scan,
      groupId: scanGroupId,
      taskId,
      optionTemplateId,
      inlineProductsById,
      addLineItemTemplateId,
      addLineItemInitializers,
      agentUpdatedAt,
      now,
    });
  }
  const scanOptionId = scanOption.id;

  if (!addLineItemTemplateId) {
    // Nothing to do!
    return scan;
  }

  return addStandardScanLineItem(
    scan,
    scanGroupId,
    taskId,
    scanOptionId,
    addLineItemTemplateId!,
    inlineProductsById,
    addLineItemInitializers,
    agentUpdatedAt,
    now,
  );
};
